import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ICourse } from '@interfaces/ICourse';
import { IAppState } from '@myStore/app.state';
import {
  ActivateCourseFailure,
  ActivateCourseRequest,
  ActivateCourseSuccess,
  DeleteCourseFailure,
  DeleteCourseRequest,
  DeleteCourseSuccess,
  ECoursesChangesActions,
  SendCreateCourseFailure,
  SendCreateCourseRequest,
  SendCreateCourseSuccess,
  SendDeactivateCourseFailure,
  SendDeactivateCourseRequest,
  SendDeactivateCourseSuccess,
  SendUpdateCourseFailure,
  SendUpdateCourseRequest,
  SendUpdateCourseSuccess,
} from '@myStore/courses-changes/courses-changes.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CoursesChangesService } from '@services/courses-changes/courses-changes.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class CoursesChangesEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _coursesChangesService: CoursesChangesService,
    private _router: Router,
    private _toastr: ToastrService,
    private _location: Location
  ) {}

  $create = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendCreateCourseRequest>(
          ECoursesChangesActions.SendCreateCourseRequest
        ),
        map(action => action.data),
        switchMap(course => {
          return this._coursesChangesService.createCourse(course).pipe(
            map((course: ICourse) => {
              this._store.dispatch(new SendCreateCourseSuccess());
              this._toastr.success(`Курс ${course.title} успешно создан!`, '', {
                timeOut: 3500,
              });
            }),
            catchError(err => {
              this._store.dispatch(new SendCreateCourseFailure(err.error));
              this._toastr.error(
                `${err?.error?.detail}`,
                'При создании курса произошла ошибка',
                {
                  timeOut: 3500,
                }
              );
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $update = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendUpdateCourseRequest>(
          ECoursesChangesActions.SendUpdateCourseRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._coursesChangesService
            .updateCourse(action.id, action.data)
            .pipe(
              map((course: ICourse) => {
                this._store.dispatch(new SendUpdateCourseSuccess());
                this._toastr.success(
                  `Курс ${course.title} успешно изменён!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
              }),
              catchError(err => {
                this._store.dispatch(new SendUpdateCourseFailure(err.error));
                this._toastr.error(
                  `${err?.error?.detail}`,
                  'При редактировании курса произошла ошибка',
                  {
                    timeOut: 3500,
                  }
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $delete = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteCourseRequest>(ECoursesChangesActions.DeleteCourseRequest),
        switchMap(action => {
          return this._coursesChangesService.delete(action.courseId).pipe(
            map(() => {
              this._store.dispatch(new DeleteCourseSuccess());
              this._toastr.success(`Курс ${action.title} успешно удалён!`, '', {
                timeOut: 3500,
              });
            }),
            catchError(err => {
              this._store.dispatch(new DeleteCourseFailure(err.error));
              this._toastr.error(
                `${err?.error?.detail}`,
                'При удалении курса возникла ошибка',
                {
                  timeOut: 3500,
                }
              );
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $deactivate = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendDeactivateCourseRequest>(
          ECoursesChangesActions.SendDeactivateCourseRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._coursesChangesService.deactivateCourse(action.id).pipe(
            map((course: ICourse) => {
              this._toastr.success(
                `Курс ${course.title} успешно деактивирован!`,
                '',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new SendDeactivateCourseSuccess());
            }),
            catchError(err => {
              this._store.dispatch(new SendDeactivateCourseFailure(err.error));
              this._toastr.error(
                `${err?.error?.detail}`,
                'При деактивации курса возникла ошибка',
                {
                  timeOut: 3500,
                }
              );
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $activate = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ActivateCourseRequest>(
          ECoursesChangesActions.ActivateCourseRequest
        ),
        switchMap(action => {
          return this._coursesChangesService.activate(action.id).pipe(
            map((course: ICourse) => {
              this._toastr.success(
                `Курс ${course.title} успешно активирован!`,
                '',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new ActivateCourseSuccess());
            }),
            catchError(err => {
              this._store.dispatch(new ActivateCourseFailure(err.error));
              this._toastr.error(
                `${err?.error?.detail}`,
                ' Не получилось активировать курс',
                {
                  timeOut: 3500,
                }
              );
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
